import { Component, computed, EventEmitter, inject, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ProgressSpinner } from 'primeng/progressspinner';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { ActivelyLinkService } from '../../services/actively-link.service';
import { SupabaseUserToolsService } from '../../services/supabase-user-tools.service';
import { MatchmakingHowItWorksComponent } from '../matchmaking-how-it-works/matchmaking-how-it-works.component';
import { FacilitiesService } from '../../services/facilities.service';
import { FacilityTabsComponent } from '../facility-tabs/facility-tabs.component';

export type MatchType = 'Men' | 'Women' | 'Mixed' | 'All';
export type FavoritesPreference = 'favorites_only' | 'anyone';

export interface MatchConfirmEvent {
  matchType: MatchType;
  favoritesOnly: boolean;
  facilityId: string | null;
}

interface MatchOption {
  label: string;
  value: MatchType;
}

interface FavoritesOption {
  label: string;
  value: FavoritesPreference;
}

@Component({
  selector: 'app-matchmaking-dialog',
  standalone: true,
  imports: [
    FormsModule,
    DialogModule,
    ButtonModule,
    SelectButtonModule,
    ProgressSpinner,
    TranslatePipe,
    MatchmakingHowItWorksComponent,
    FacilityTabsComponent,
  ],
  templateUrl: './matchmaking-dialog.component.html',
  styleUrl: './matchmaking-dialog.component.scss',
})
export class MatchmakingDialogComponent implements OnChanges {
  private readonly activelyLinkService = inject(ActivelyLinkService);
  private readonly userToolsService = inject(SupabaseUserToolsService);
  private readonly router = inject(Router);
  private readonly facilitiesService = inject(FacilitiesService);

  private static readonly MIN_FAVORITES_FOR_ONLY = 3;

  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<MatchConfirmEvent>();

  showHowItWorks = false;
  selectedMatchType: MatchType = 'All';
  selectedFavoritesPreference: FavoritesPreference = 'anyone';
  selectedFacilityId: string | null = null;
  readonly matchmakingFacilities = computed(() =>
    this.facilitiesService
      .facilities()
      .filter((f) => f.matchmaking_enabled && f.has_calendar !== false)
  );
  readonly showFacilityPicker = computed(
    () => this.matchmakingFacilities().length > 0
  );
  earliestSlotTime = signal<string | null>(null);
  loadingSlot = signal(false);
  favoritesCount = signal<number>(0);
  noSlotsAvailable = computed(() => !this.loadingSlot() && !this.earliestSlotTime());
  notEnoughFavorites = computed(
    () => this.favoritesCount() < MatchmakingDialogComponent.MIN_FAVORITES_FOR_ONLY
  );

  matchOptions: MatchOption[] = [
    { label: 'Men', value: 'Men' },
    { label: 'Women', value: 'Women' },
    { label: 'Mixed', value: 'Mixed' },
    { label: 'All', value: 'All' },
  ];

  favoritesOptions: FavoritesOption[] = [
    { label: 'Favorites Only', value: 'favorites_only' },
    { label: 'Anyone', value: 'anyone' },
  ];

  ngOnChanges(changes: SimpleChanges) {
    if (changes['visible'] && this.visible) {
      this.loadEarliestSlot();
      this.loadFavoritesCount();
      this.initFacilities();
    }
  }

  private async initFacilities() {
    await this.facilitiesService.init();
    const facs = this.matchmakingFacilities();
    if (facs.length && !this.selectedFacilityId) {
      this.selectedFacilityId = facs[0].id;
    }
  }

  private async loadEarliestSlot() {
    this.loadingSlot.set(true);
    const result = await this.activelyLinkService.getEarliestSlot();
    if (result.earliestSlot) {
      const time = result.earliestSlot.startTime.substring(11, 16);
      this.earliestSlotTime.set(time);
    } else {
      this.earliestSlotTime.set(null);
    }
    this.loadingSlot.set(false);
  }

  private async loadFavoritesCount() {
    const favorites = await this.userToolsService.loadUserFavorites();
    this.favoritesCount.set(favorites.length);
    if (this.notEnoughFavorites() && this.selectedFavoritesPreference === 'favorites_only') {
      this.selectedFavoritesPreference = 'anyone';
    }
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onConfirm() {
    this.confirm.emit({
      matchType: this.selectedMatchType,
      favoritesOnly: this.selectedFavoritesPreference === 'favorites_only',
      facilityId: this.selectedFacilityId,
    });
    this.onClose();
  }

  openHowItWorks() {
    this.onClose();
    setTimeout(() => {
      this.showHowItWorks = true;
    }, 300);
  }

  goToPreferences() {
    this.onClose();
    this.router.navigate(['/player-preferences']);
  }
}
